import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { useT } from '../../i18n';
import './Toast.css';

export type ToastVariant = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  variant: ToastVariant;
  title?: string;
  message: string;
  leaving?: boolean;
}

interface ToastApi {
  /** Show a toast. Returns its id so callers can dismiss it early. */
  show: (message: string, opts?: { variant?: ToastVariant; title?: string; duration?: number }) => number;
  success: (message: string, title?: string) => number;
  error: (message: string, title?: string) => number;
  info: (message: string, title?: string) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle2 size={18} />,
  error: <AlertCircle size={18} />,
  info: <Info size={18} />,
};

/** Errors stay up longer than confirmations — they carry more to read. */
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 3500,
  info: 4000,
  error: 6000,
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { t: tr } = useT();
  const nextId = useRef(1);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback((id: number) => {
    // Play the exit animation, then unmount.
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    const timer = setTimeout(() => remove(id), 160);
    timers.current.set(id, timer);
  }, [remove]);

  const show = useCallback<ToastApi['show']>((message, opts) => {
    const variant = opts?.variant ?? 'info';
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, variant, title: opts?.title, message }]);
    const duration = opts?.duration ?? DEFAULT_DURATION[variant];
    const timer = setTimeout(() => dismiss(id), duration);
    timers.current.set(id, timer);
    return id;
  }, [dismiss]);

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (message, title) => show(message, { variant: 'success', title }),
    error: (message, title) => show(message, { variant: 'error', title }),
    info: (message, title) => show(message, { variant: 'info', title }),
    dismiss,
  }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {ReactDOM.createPortal(
        <div className="toast-viewport">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`toast toast--${t.variant}${t.leaving ? ' is-leaving' : ''}`}
              // Errors interrupt; the rest wait for a pause in speech.
              role={t.variant === 'error' ? 'alert' : 'status'}
              aria-live={t.variant === 'error' ? 'assertive' : 'polite'}
            >
              <span className="toast-icon" aria-hidden="true">{ICONS[t.variant]}</span>
              <div className="toast-body">
                {t.title && <div className="toast-title">{t.title}</div>}
                <div className="toast-message">{t.message}</div>
              </div>
              <button
                type="button"
                className="toast-dismiss"
                onClick={() => dismiss(t.id)}
                aria-label={tr('toast.dismiss')}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

/**
 * Access the toast API. Falls back to a console-only shim when used outside a
 * provider so unit tests rendering a component in isolation don't crash.
 */
export const useToast = (): ToastApi => {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  return {
    show: () => 0,
    success: () => 0,
    error: (message) => { console.error('[toast]', message); return 0; },
    info: () => 0,
    dismiss: () => {},
  };
};
